import axioslib, { AxiosInstance, AxiosRequestConfig } from "axios";
import { AuthInterceptor } from "./AuthInterceptor";
import VersionInterceptor from "@/api/VersionInterceptor";
import {
  IDeliveryDateInformationRequest,
  IDeliveryDateInformationResponse,
  Logger,
} from "@iloq/shared";
import { IOrder, IOrderDraftDeleteResponse } from "@iloq/shared";
import { IOrderingShipment } from "@iloq/shared/IOrderingShipment";

export default class OrderingApi {
  private readonly axios: AxiosInstance;

  constructor() {
    this.axios = axioslib.create();
    AuthInterceptor.setAuthInterceptors(this.axios);
    VersionInterceptor.setVersionInterceptor(this.axios);
  }
  getDeliveryDateInformation(
    deliveryDateInformationRequest: IDeliveryDateInformationRequest
  ): Promise<IDeliveryDateInformationResponse> {
    Logger.debug(
      `OrderingApi.getDeliveryDateInformation, deliveryDateInformationRequest=${JSON.stringify(
        deliveryDateInformationRequest
      )}`
    );
    const options: AxiosRequestConfig = {
      method: "POST",
      url: `/api/v1/ordering/deliverydate-information`,
      data: deliveryDateInformationRequest,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("OrderingApi.getDeliveryDateInformation failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("OrderingApi.getDeliveryDateInformation result data:", result.data);
        const response: IDeliveryDateInformationResponse = {
          deliveryDate: new Date(result.data.deliveryDate),
          deliveryDelay: result.data.deliveryDelay,
        };
        return response;
      });
  }
  storeOrderAsDraft(order: IOrder): Promise<IOrder> {
    Logger.debug(`OrderingApi.storeOrdersAsDraft=${order}`);
    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/ordering/order-draft/",
      data: order,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("OrderingApi.storeOrderAsDraft failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("OrderingApi.storeOrderAsDraft result data:", result.data);
        return result.data;
      });
  }

  deleteOrderDraft(order: IOrder): Promise<IOrderDraftDeleteResponse> {
    Logger.debug(`OrderingApi.deleteOrderDraft=${order}`);

    const options: AxiosRequestConfig = {
      method: "DELETE",
      url: "/api/v1/ordering/order-draft/",
      data: order,
    };
    return this.axios(options)
      .catch((error) => {
        Logger.error("OrderingApi.deleteOrderDraft failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("OrderingApi.deleteOrderDraft result data:", result.data);
        return result.data;
      });
  }

  sendOrder(order: IOrder): Promise<IOrder> {
    Logger.debug("OrderingApi.handling sendOrder: ", order);
    const options: AxiosRequestConfig = {
      method: "POST",
      url: "/api/v1/ordering/order/",
      data: order,
    };
    if (order.rows.length > 0) {
      return this.axios(options)
        .catch((error) => {
          Logger.error("OrderingApi.sendOrder failed:", error);
          return Promise.reject(error);
        })
        .then((result) => {
          Logger.debug("OrderingApi.sendOrder result data:", result.data);
          return result.data;
        });
    }
    return Promise.reject(new Error("Cannot create order with zero rows"));
  }

  fetchOrders(): Promise<IOrder[]> {
    const options: AxiosRequestConfig = {
      method: "GET",
      url: "/api/v1/ordering/orders/",
    };
    Logger.debug("OrderingApi.fetchOrders");
    return this.axios(options)
      .catch((error) => {
        Logger.error("OrderingApi.fetchOrders failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("OrderingApi.fetchOrders result data:", result.data);
        return result.data;
      });
  }

  fetchOrdersApplicableForSalesReturns(): Promise<IOrder[]> {
    const options: AxiosRequestConfig = {
      method: "GET",
      url: "/api/v1/ordering/orders-applicable-for-sales-returns/",
    };
    Logger.debug("OrderingApi.fetchOrdersApplicableForSalesReturns");
    return this.axios(options)
      .catch((error) => {
        Logger.error("OrderingApi.fetchOrdersApplicableForSalesReturns failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("OrderingApi.fetchOrdersApplicableForSalesReturns result data:", result.data);
        return result.data;
      });
  }
  fetchOrderingShipments({
    orderNumber,
    deliveryDetails: { deliveryTypeCode },
  }: IOrder): Promise<IOrderingShipment[]> {
    const options: AxiosRequestConfig = {
      method: "GET",
      url: "/api/v1/ordering/shipments/",
      params: {
        orderNumber: `${orderNumber}`,
        deliveryType:
          deliveryTypeCode && ["SEKO", "UPSN"].includes(deliveryTypeCode)
            ? deliveryTypeCode
            : undefined,
      },
    };
    Logger.debug("OrderingApi.fetchOrderingShipments");
    return this.axios(options)
      .catch((error) => {
        Logger.error("OrderingApi.fetchOrderingShipments failed:", error);
        return Promise.reject(error);
      })
      .then((result) => {
        Logger.debug("OrderingApi.fetchOrderingShipments result data:", result.data);
        return result.data;
      });
  }
}
